// frontend/src/pages/Career.js
import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getUserCareer, addDevPlanItem, updateDevPlanItem, deleteDevPlanItem, getUsers } from '../services/api';
import { Card, Avatar, Badge, Btn, Modal, Field, Input, Select, SectionTitle, Spinner } from '../components/UI';
import PageHeader from '../components/PageHeader';

const STATUS_COLOR  = { complete: 'green', in_progress: 'blue', upcoming: 'gray' };
const STATUS_LABELS = { complete: 'Done', in_progress: 'In progress', upcoming: 'Upcoming' };

function LadderTrack({ levels = [], current, target }) {
  if (!levels.length) return null;
  const currentIdx = levels.findIndex(l => l.level_name === current);
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', marginBottom: 24 }}>
      {levels.map((level, i) => {
        const isAchieved = i < currentIdx;
        const isCurrent  = i === currentIdx;
        const heightPct  = 60 + (i / (levels.length - 1)) * 120;
        return (
          <div key={level.id} style={{
            flex: 1, borderRadius: 8, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'flex-end', padding: '8px 6px',
            height: heightPct,
            background: isAchieved ? 'var(--navy-600)' : isCurrent ? 'var(--navy-50)' : 'var(--gray-100)',
            border: `0.5px solid ${isCurrent ? 'var(--navy-500)' : 'transparent'}`,
            transition: 'all 0.3s',
          }}>
            <div style={{ fontSize: 11, fontWeight: 500, textAlign: 'center', color: isAchieved ? '#fff' : isCurrent ? 'var(--navy-600)' : 'var(--text-3)', lineHeight: 1.3 }}>
              {level.level_name.replace(' Engineer','').replace(' Manager','').replace(' PM','')}
            </div>
            {isAchieved && <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>✓</div>}
            {isCurrent && <div style={{ fontSize: 9, color: 'var(--navy-600)', marginTop: 2 }}>Current</div>}
          </div>
        );
      })}
    </div>
  );
}

export default function Career() {
  const { user } = useAuth();
  const [users,     setUsers]     = useState([]);
  const [selectedId,setSelectedId]= useState(null);
  const [careerData,setCareerData]= useState(null);
  const [loading,   setLoading]   = useState(false);
  const [showAdd,   setShowAdd]   = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [editItem,  setEditItem]  = useState(null);
  const [form, setForm] = useState({ title: '', category: '', due_date: '', status: 'upcoming' });

  useEffect(() => {
    if (['admin','manager','dept_head'].includes(user?.role)) {
      import('../services/api').then(({ getUsers }) => getUsers().then(u => {
        setUsers(u.filter(x => x.id !== user?.id));
        if (u.length) setSelectedId(u.find(x => x.id !== user?.id)?.id);
      }));
    } else {
      setSelectedId(user?.id);
    }
  }, [user]);

  useEffect(() => {
    if (!selectedId) return;
    setLoading(true);
    getUserCareer(selectedId).then(setCareerData).catch(console.error).finally(() => setLoading(false));
  }, [selectedId]);

  const openAdd = () => { setEditItem(null); setForm({ title: '', category: '', due_date: '', status: 'upcoming' }); setShowAdd(true); };
  const openEdit = item => { setEditItem(item); setForm({ title: item.title, category: item.category || '', due_date: item.due_date?.slice(0,10) || '', status: item.status }); setShowAdd(true); };

  const handleSave = async e => {
    e.preventDefault(); setSaving(true);
    try {
      if (editItem) await updateDevPlanItem(editItem.id, form);
      else await addDevPlanItem({ ...form, user_id: selectedId });
      const d = await getUserCareer(selectedId);
      setCareerData(d); setShowAdd(false);
    } catch (err) { alert('Error saving'); }
    finally { setSaving(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm('Archive this item? It will be hidden from the system.')) return;
    await deleteDevPlanItem(id);
    const d = await getUserCareer(selectedId);
    setCareerData(d);
  };

  const handleToggle = async item => {
    const next = item.status === 'complete' ? 'in_progress' : item.status === 'in_progress' ? 'complete' : 'in_progress';
    await updateDevPlanItem(item.id, { ...item, status: next });
    const d = await getUserCareer(selectedId);
    setCareerData(d);
  };

  const selectedUser = users.find(u => u.id === selectedId);
  const career = careerData?.career;
  const plan   = careerData?.dev_plan || [];
  const levels = careerData?.levels   || [];
  const done   = plan.filter(p => p.status === 'complete').length;

  return (
    <div style={{ padding: '20px clamp(16px, 3vw, 32px)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <PageHeader moduleKey="career" helpTask="career-ladder" />
      </div>

      {/* Person selector (manager/admin) */}
      {['admin','manager','dept_head'].includes(user?.role) && users.length > 0 && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 24 }}>
          <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 500 }}>Employee:</span>
          <select value={selectedId || ''} onChange={e => setSelectedId(Number(e.target.value))}
            style={{ padding: '7px 12px', fontSize: 13, border: '0.5px solid var(--border-md)', borderRadius: 'var(--radius-md)', background: 'var(--surface)', color: 'var(--text-1)', minWidth: 200 }}>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      )}

      {loading && <Spinner />}

      {!loading && careerData && (
        <>
          {/* Career card */}
          <Card style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <Avatar initials={selectedUser?.avatar_initials || user?.avatar_initials} size={44} />
              <div>
                <div style={{ fontSize: 15, fontWeight: 500 }}>{selectedUser?.name || user?.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{career?.track || 'Engineering'} track · {career?.current_level || '—'}</div>
              </div>
              {career && (
                <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                  <div style={{ fontSize: 28, fontWeight: 500, color: 'var(--navy-600)' }}>{career.progress_pct}%</div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)' }}>towards {career.target_level}</div>
                </div>
              )}
            </div>

            {/* Ladder visual */}
            <LadderTrack levels={levels} current={career?.current_level} target={career?.target_level} />

            {/* Progress bar to next level */}
            {career && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-3)', marginBottom: 5 }}>
                  <span>{career.current_level}</span>
                  <span>{career.target_level}</span>
                </div>
                <div style={{ height: 8, background: 'var(--gray-100)', borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${career.progress_pct}%`, background: 'var(--navy-500)', borderRadius: 10, transition: 'width 0.4s' }} />
                </div>
              </div>
            )}
          </Card>

          {/* Development plan */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <SectionTitle>Development plan · {done}/{plan.length} complete</SectionTitle>
            <Btn size="sm" onClick={openAdd}>+ Add item</Btn>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {plan.map(item => (
              <Card key={item.id} style={{ padding: '12px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {/* Toggle checkbox */}
                  <button onClick={() => handleToggle(item)} style={{
                    width: 22, height: 22, borderRadius: '50%', flexShrink: 0, cursor: 'pointer',
                    border: item.status === 'complete' ? 'none' : '1.5px solid var(--border-md)',
                    background: item.status === 'complete' ? 'var(--navy-500)' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, color: '#fff',
                  }}>
                    {item.status === 'complete' ? '✓' : ''}
                  </button>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, textDecoration: item.status === 'complete' ? 'line-through' : 'none', color: item.status === 'complete' ? 'var(--text-3)' : 'var(--text-1)' }}>
                      {item.title}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
                      {item.category && <span>{item.category} · </span>}
                      {item.due_date && <span>Due {new Date(item.due_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                    </div>
                  </div>

                  <Badge color={STATUS_COLOR[item.status]}>{STATUS_LABELS[item.status]}</Badge>
                  <Btn variant="ghost" size="sm" onClick={() => openEdit(item)}>Edit</Btn>
                  <Btn variant="ghost" size="sm" onClick={() => handleDelete(item.id)} style={{ color: 'var(--red-500)' }}>✕</Btn>
                </div>
              </Card>
            ))}
            {plan.length === 0 && (
              <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-3)', fontSize: 13 }}>
                No development plan items yet. Add one to start tracking growth.
              </div>
            )}
          </div>
        </>
      )}

      {/* Add/Edit modal */}
      <Modal open={showAdd} onClose={() => setShowAdd(false)} title={editItem ? 'Edit plan item' : 'Add development item'} width={420}>
        <form onSubmit={handleSave}>
          <Field label="Goal / milestone" required>
            <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Present to exec team" />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Category">
              <Input value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} placeholder="e.g. Leadership" />
            </Field>
            <Field label="Due date">
              <Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} />
            </Field>
          </div>
          <Field label="Status">
            <Select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
              <option value="upcoming">Upcoming</option>
              <option value="in_progress">In progress</option>
              <option value="complete">Complete</option>
            </Select>
          </Field>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn variant="secondary" onClick={() => setShowAdd(false)}>Cancel</Btn>
            <Btn disabled={saving || !form.title}>{saving ? 'Saving…' : editItem ? 'Save changes' : 'Add item'}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
